import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { X, Upload, Eye, ArrowLeft, Check, Loader, ZoomIn, ZoomOut, Crop } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

// Cores VIPLOC
const colors = {
  navy: '#2B3E5C',
  orange: '#FF9933',
};

/**
 * Cria uma Image a partir de URL
 */
function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

/**
 * Recorta imagem via Canvas API
 */
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

/**
 * PhotoEditorModal - Editor de fotos com recorte e substituição
 */
const PhotoEditorModal = ({ photo, inspectionId, onSave, onClose }) => {
  const fileInputRef = useRef(null);

  // Fonte da imagem (original ou replacement)
  const originalSrc = photo.photo_url || photo.url || `/uploads/inspections/${photo.filename}`;
  const [imageSrc, setImageSrc] = useState(originalSrc);
  const [isReplacement, setIsReplacement] = useState(false);

  // Crop state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Flow state
  const [step, setStep] = useState('crop'); // 'crop' | 'preview'
  const [croppedPreview, setCroppedPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Upload nova foto
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande (max 10MB)');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setImageSrc(objectUrl);
    setIsReplacement(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setStep('crop');
  };

  // Gerar preview do recorte
  const handlePreview = async () => {
    if (!croppedAreaPixels) {
      toast.error('Ajuste o recorte da foto');
      return;
    }

    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const previewUrl = URL.createObjectURL(blob);
      setCroppedPreview(previewUrl);
      setStep('preview');
    } catch (err) {
      console.error('Erro ao gerar preview:', err);
      toast.error('Erro ao gerar preview. Tente novamente.');
    }
  };

  // Aprovar e salvar
  const handleApprove = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);

    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append('photo', blob, `cropped-${Date.now()}.jpg`);

      const response = await api.put(
        `/api/inspections/${inspectionId}/photos/${photo.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data?.success) {
        toast.success('Foto atualizada!');
        onSave(response.data.data);
      } else {
        toast.error(response.data?.message || 'Erro ao salvar');
      }
    } catch (error) {
      console.error('Erro ao salvar foto:', error);
      toast.error('Erro ao salvar: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Voltar do preview para o crop
  const handleBack = () => {
    if (croppedPreview) {
      URL.revokeObjectURL(croppedPreview);
      setCroppedPreview(null);
    }
    setStep('crop');
  };

  // Fechar modal
  const handleClose = () => {
    if (croppedPreview) URL.revokeObjectURL(croppedPreview);
    if (isReplacement && imageSrc.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4"
      style={{ zIndex: 70 }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: colors.navy }}>
          <div className="flex items-center gap-2 text-white">
            <Crop className="w-5 h-5" />
            <span className="font-bold text-lg">
              {step === 'crop' ? 'Editor de Foto' : 'Confirmar Recorte'}
            </span>
          </div>
          <button onClick={handleClose} className="text-white hover:text-gray-300 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {step === 'crop' && (
          <>
            {/* Crop Area */}
            <div className="relative" style={{ height: '400px', backgroundColor: '#1a1a1a' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={true}
                cropShape="rect"
              />
            </div>

            {/* Zoom Controls */}
            <div className="px-5 py-3 bg-gray-50 border-t border-b">
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-gray-500" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 accent-orange-500"
                />
                <ZoomIn className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500 font-medium w-12 text-right">{zoom.toFixed(1)}x</span>
              </div>
            </div>

            {/* Item info */}
            {photo.item_name && (
              <div className="px-5 py-2 bg-gray-50 text-sm text-gray-600">
                <strong>Item:</strong> {photo.item_name}
                {photo.section_name && <> | <strong>Secao:</strong> {photo.section_name}</>}
              </div>
            )}

            {/* Action Buttons */}
            <div className="px-5 py-4 flex items-center justify-between">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all hover:bg-gray-50"
                  style={{ borderColor: colors.navy, color: colors.navy }}
                >
                  <Upload className="w-4 h-4" />
                  Upload Nova Foto
                </button>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-100 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePreview}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: colors.orange }}
                >
                  <Eye className="w-4 h-4" />
                  Visualizar
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            {/* Preview Image */}
            <div className="p-6 flex items-center justify-center" style={{ backgroundColor: '#f8f8f8', minHeight: '350px' }}>
              {croppedPreview && (
                <img
                  src={croppedPreview}
                  alt="Preview recortada"
                  className="max-w-full max-h-[350px] object-contain rounded-lg shadow-lg"
                />
              )}
            </div>

            {/* Approval Buttons */}
            <div className="px-5 py-4 flex items-center justify-between border-t">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-100 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>
              <button
                onClick={handleApprove}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#22c55e' }}
              >
                {saving ? (
                  <><Loader className="w-4 h-4 animate-spin" /> Salvando...</>
                ) : (
                  <><Check className="w-4 h-4" /> Aprovar e Salvar</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhotoEditorModal;
